import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { localization as commonLocalization } from '@sitebuilder/common';
import { types as coreTypes } from '@sitebuilder/core';
import { ListApiClient } from '../api/listApiClient';
import { ThunkResult } from './index';

export interface StateShape {
    listItemCopySuccess?: string;
    listItemCopyError?: any | null;
    listItemCopyLoading?: boolean;
    listItemDeleteLoading?: boolean;
    listItemDeleteSuccess?: string;
    listItemDeleteError?: any | null;
    listItemMoveLoading?: boolean;
    listItemMoveSuccess?: string;
    listItemMoveError?: any | null;
    listItemTagDeleteLoading?: boolean;
    listItemTagDeleteSuccess?: string;
    listItemTagDeleteError?: any | null;
    listItemTagDeletetag?: string;
    listItemTagAddLoading?: boolean;
    listItemTagAddSuccess?: string;
    listItemTagAddError?: any | null;
    listItemTagAddtag?: string;
}

export const defaultState: StateShape = {
    listItemCopySuccess: '',
    listItemCopyError: null,
    listItemCopyLoading: false,
    listItemDeleteLoading: false,
    listItemDeleteSuccess: '',
    listItemDeleteError: null,
    listItemMoveLoading: false,
    listItemMoveSuccess: '',
    listItemMoveError: null,
    listItemTagDeleteLoading: false,
    listItemTagDeleteSuccess: '',
    listItemTagDeleteError: null,
    listItemTagDeletetag: '',
    listItemTagAddLoading: false,
    listItemTagAddSuccess: '',
    listItemTagAddError: null,
    listItemTagAddtag: '',
};

const apiClient = new ListApiClient();

export const actionCreators = {
    copyListItem: (
        sourceFileId: string,
        destinationFileId: string,
        listType: coreTypes.listType,
        libraryName?: string
    ): ThunkResult<Promise<boolean | void>> => {
        return async (dispatch, getState) => {
            const state = getState();
            const locale = state?.templateEditor?.documentMetadata?.locale || 'en-US';
            const appI18n = commonLocalization.getLocalization(locale);

            const accessToken = state.auth.user?.access_token;
            dispatch(listsReducerActions.COPY_LISTITEM_START());

            try {
                await apiClient.copyListItem(
                    sourceFileId,
                    destinationFileId,
                    listType,
                    libraryName,
                    {
                        accessToken,
                    }
                );

                dispatch(
                    listsReducerActions.COPY_LISTITEM_END({
                        success: appI18n.t('list_item_copy_success_message'),
                    })
                );
                return true;
            } catch (err: any) {
                console.error(err);
                dispatch(
                    listsReducerActions.COPY_LISTITEM_END({
                        error: { message: err.message },
                    })
                );
                return false;
            }
        };
    },
    deleteListItems: (
        fileIds: string[],
        listType: coreTypes.listType,
        libraryName?: string
    ): ThunkResult<Promise<boolean | void>> => {
        return async (dispatch, getState) => {
            const state = getState();
            const locale = state?.templateEditor?.documentMetadata?.locale || 'en-US';
            const appI18n = commonLocalization.getLocalization(locale);

            const accessToken = state.auth.user?.access_token;
            dispatch(listsReducerActions.DELETE_LISTITEM_START());

            try {
                for (const fileId of fileIds) {
                    await apiClient.deleteListItem(fileId, listType, libraryName, {
                        accessToken,
                    });
                }

                dispatch(
                    listsReducerActions.DELETE_LISTITEM_END({
                        success: appI18n.t('list_item_delete_success_message'),
                    })
                );
                return true;
            } catch (err: any) {
                console.error(err);
                dispatch(
                    listsReducerActions.DELETE_LISTITEM_END({
                        error: { message: err.message },
                    })
                );
                return false;
            }
        };
    },
    resetStatusMessages: (): ThunkResult<Promise<void>> => {
        return async (dispatch) => {
            dispatch(listsReducerActions.RESET_STATUS_MESSAGES());
        };
    },
    moveListItem: (
        sourceFileId: string,
        destinationFileId: string,
        listType: coreTypes.listType,
        libraryName?: string
    ): ThunkResult<Promise<boolean | void>> => {
        return async (dispatch, getState) => {
            const state = getState();
            const locale = state?.templateEditor?.documentMetadata?.locale || 'en-US';
            const appI18n = commonLocalization.getLocalization(locale);

            const accessToken = state.auth.user?.access_token;
            dispatch(listsReducerActions.MOVE_LISTITEM_START());

            try {
                await apiClient.moveListItem(
                    sourceFileId,
                    destinationFileId,
                    listType,
                    libraryName,
                    {
                        accessToken,
                    }
                );

                dispatch(
                    listsReducerActions.MOVE_LISTITEM_END({
                        success: appI18n.t('list_item_move_success_message'),
                    })
                );
                return true;
            } catch (err: any) {
                console.error(err);
                dispatch(
                    listsReducerActions.MOVE_LISTITEM_END({
                        error: { message: err.message },
                    })
                );
                return false;
            }
        };
    },
    deleteListTagItem: (
        sourceFileId: string,
        tag: string,
        listType: coreTypes.listType,
        libraryName?: string
    ): ThunkResult<Promise<boolean | void>> => {
        return async (dispatch, getState) => {
            const state = getState();
            const locale = state?.templateEditor?.documentMetadata?.locale || 'en-US';
            const appI18n = commonLocalization.getLocalization(locale);

            const accessToken = state.auth.user?.access_token;
            dispatch(listsReducerActions.DELETE_LISTITEMTAG_START());

            try {
                await apiClient.deleteListTagItem(sourceFileId, tag, listType, libraryName, {
                    accessToken,
                });

                dispatch(
                    listsReducerActions.DELETE_LISTITEMTAG_END({
                        success: appI18n.t('list_item_move_success_message'),
                        tag: tag,
                    })
                );
                return true;
            } catch (err: any) {
                console.error(err);
                dispatch(
                    listsReducerActions.DELETE_LISTITEMTAG_END({
                        error: { message: err.message },
                    })
                );
                return false;
            }
        };
    },
    addListTagItem: (
        sourceFileId: string,
        tag: string,
        listType: coreTypes.listType,
        libraryName?: string
    ): ThunkResult<Promise<boolean | void>> => {
        return async (dispatch, getState) => {
            const state = getState();
            const locale = state?.templateEditor?.documentMetadata?.locale || 'en-US';
            const appI18n = commonLocalization.getLocalization(locale);

            const accessToken = state.auth.user?.access_token;
            dispatch(listsReducerActions.ADD_LISTITEMTAG_START());

            try {
                await apiClient.addListTagItem(sourceFileId, tag, listType, libraryName, {
                    accessToken,
                });

                dispatch(
                    listsReducerActions.ADD_LISTITEMTAG_END({
                        success: appI18n.t('list_item_move_success_message'),
                        tag: tag,
                    })
                );
                return true;
            } catch (err: any) {
                console.error(err);
                dispatch(
                    listsReducerActions.ADD_LISTITEMTAG_END({
                        error: { message: err.message },
                    })
                );
                return false;
            }
        };
    },
};

const templateSlice = createSlice({
    name: 'lists',
    initialState: defaultState,
    reducers: {
        COPY_LISTITEM_START(draft) {
            draft.listItemCopyLoading = true;
            draft.listItemCopyError = null;
            draft.listItemCopySuccess = '';
        },
        COPY_LISTITEM_END(draft, action: PayloadAction<{ success?: string; error?: any }>) {
            const { success, error } = action.payload;
            draft.listItemCopyLoading = false;
            draft.listItemCopySuccess = success || '';
            draft.listItemCopyError = error;
        },
        DELETE_LISTITEM_START(draft) {
            draft.listItemDeleteLoading = true;
            draft.listItemDeleteError = null;
            draft.listItemDeleteSuccess = '';
        },
        DELETE_LISTITEM_END(draft, action: PayloadAction<{ success?: string; error?: any }>) {
            const { success, error } = action.payload;
            draft.listItemDeleteLoading = false;
            draft.listItemDeleteSuccess = success || '';
            draft.listItemDeleteError = error;
        },
        RESET_STATUS_MESSAGES(draft) {
            draft.listItemCopySuccess = '';
            draft.listItemCopyError = null;
            draft.listItemCopyLoading = false;
            draft.listItemDeleteLoading = false;
            draft.listItemDeleteSuccess = '';
            draft.listItemDeleteError = null;
        },
        MOVE_LISTITEM_START(draft) {
            draft.listItemMoveLoading = true;
            draft.listItemMoveError = null;
            draft.listItemMoveSuccess = '';
        },
        MOVE_LISTITEM_END(draft, action: PayloadAction<{ success?: string; error?: any }>) {
            const { success, error } = action.payload;
            draft.listItemMoveLoading = false;
            draft.listItemMoveSuccess = success || '';
            draft.listItemMoveError = error;
        },
        DELETE_LISTITEMTAG_START(draft) {
            draft.listItemTagDeleteLoading = true;
            draft.listItemTagDeleteError = null;
            draft.listItemTagDeleteSuccess = '';
            draft.listItemTagDeletetag = '';
        },
        DELETE_LISTITEMTAG_END(
            draft,
            action: PayloadAction<{
                success?: string;
                error?: any;
                tag?: string;
            }>
        ) {
            const { success, error, tag } = action.payload;
            draft.listItemTagDeleteLoading = false;
            draft.listItemTagDeleteSuccess = success || '';
            draft.listItemTagDeleteError = error;
            draft.listItemTagDeletetag = tag || '';
        },
        ADD_LISTITEMTAG_START(draft) {
            draft.listItemTagAddLoading = true;
            draft.listItemTagAddError = null;
            draft.listItemTagAddSuccess = '';
            draft.listItemTagAddtag = '';
        },
        ADD_LISTITEMTAG_END(
            draft,
            action: PayloadAction<{
                success?: string;
                error?: any;
                tag?: string;
            }>
        ) {
            const { success, error, tag } = action.payload;
            draft.listItemTagAddLoading = false;
            draft.listItemTagAddSuccess = success || '';
            draft.listItemTagAddError = error;
            draft.listItemTagAddtag = tag || '';
        },
    },
});

export const listsReducerActions: any = templateSlice.actions;
export const listsReducer = templateSlice.reducer;
